import { useEffect, useRef, useState } from 'preact/hooks';
import type { ItemDoc, MemberDoc } from '../data';
import { fmtLong, leaderWords, outcomeWords } from '../format';
import './overlay.css';

// Share, on every item pop-up and member card, desktop and phone (Dan, 30 Sep 2026: "so visitors can
// share to social media, on a blog or via email"; his networks and the plain link for blogs, 1 Oct).
// The brief's zero-visitor-data rule shapes it: no widget, script, frame, logo or pixel from any network.
// Each network is a plain link that opens its own page in a new tab, so nothing reaches it unless the
// visitor presses it; Copy link is the browser's clipboard, Other apps the device's own share menu. The
// address is always the site's own, with no tracking parameters, and the wording always names the site
// (Dan, 30 Sep: "so folks know where it came from"). No network's colour or logo: blue reads as a party
// colour (the phone's Find box lost Chrome's blue clear button for the same reason).

export const ORIGIN = 'https://votesonrecord.org';
const SITE = 'Votes on Record';
const TEXT_MAX = 240;       // the wording before the link: X allows 280 with the link, Bluesky 300 with it

/** The tab's title, in the words the server writes into the same page (server pages.Shell.render): the page's own
 *  title, then the site's name. Search engines read a page after this code has run, so this is the title they list:
 *  on 12 Oct 2026 Google listed every member page as plain "Votes on Record", because the app had set that over the
 *  server's "Chuck Grassley · Votes on Record". */
export function pageTitle(name?: string | null) {
  document.title = name ? `${name} · ${SITE}` : SITE;
}

export interface ShareWhat {
  path: string;             // '/item/119-hr-1'
  title: string;            // the link preview's own title: 'H.R. 1: One Big Beautiful Bill Act', 'Chuck Grassley'
  detail: string | null;    // one line of facts for the email
  card: string | null;      // the share card's address, for Instagram, which takes a picture and no link
  file: string;             // the picture's name when saved
  text?: string;            // the wording, when it is not "<title>, on Votes on Record" (the site itself)
}

/** The site itself, from the header's Share and the phone's Menu (Dan, 9 Oct 2026): its name and the line under it
 *  (Chrome.tsx TAGLINE), the home page's address, and the site's own card (web/public/share-card.png, the worker's
 *  `site-card`), which Instagram takes. */
export function siteShare(tagline: string): ShareWhat {
  return {
    path: '/', title: SITE, detail: tagline, card: '/share-card.png', file: 'votes-on-record.png',
    text: `${SITE}: ${tagline.charAt(0).toLowerCase()}${tagline.slice(1)}`,
  };
}

/** The link preview's title (server pages.item_preview) and the outcome with its day, as the share card dates it
 *  (Grid.tsx outcomeDay: a law or a veto on the President's day, a law over a veto with none). */
export function itemShare(doc: ItemDoc): ShareWhat {
  const p = doc.president, law = doc.outcome === 'became_law' || doc.outcome === 'law_without_signature';
  const day = law && (p.status === 'signed' || p.status === 'without_signature') ? p.date
    : doc.outcome === 'vetoed' && p.status === 'vetoed' ? p.date
    : doc.outcome === 'became_law' ? null
    : [doc.chambers.house.date, doc.chambers.senate.date].filter(Boolean).sort().pop() ?? null;
  return {
    path: '/item/' + doc.id,
    title: doc.title ? `${doc.number}: ${doc.title}` : doc.number,
    detail: outcomeWords(doc.outcome, doc.kind) + (day ? ', ' + fmtLong(day) : '') + (doc.law_number ? ` · ${doc.law_number}` : '') + '.',
    card: doc.card ? `/data/${doc.card.image}?v=${doc.card.v}` : null,
    file: `votes-on-record-${doc.id}.png`,
  };
}

const OFFICE: Record<string, string> = { rep: 'Representative', sen: 'Senator', president: 'President', vp: 'Vice President' };

/** The person's name, and the office shown on the card in the link preview's words (server pages.member_preview). */
export function memberShare(doc: MemberDoc, m: MemberDoc['memberships'][number] | undefined): ShareWhat {
  const who = m ? [OFFICE[m.role] ?? m.role, m.seat].filter(Boolean).join(', ') + (m.party ? ` (${m.party})` : '') : null;
  const detail = m ? [who, leaderWords(m.leader), m.left ? `Left office ${fmtLong(m.to)}` : null].filter(Boolean).join(' · ') + '.' : null;
  return {
    path: '/member/' + doc.id, title: doc.full, detail,
    card: doc.card ? `/data/${doc.card.image}?v=${doc.card.v}` : null,
    file: `votes-on-record-${doc.id}.png`,
  };
}

/** A title cut at a word, so the wording and the link fit every network's limit. */
function clip(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n - 1).replace(/\s+\S*$/, '').replace(/[,;:]$/, '') + '…';
}

const enc = encodeURIComponent;

/** What is sent where: the wording ("<title>, on Votes on Record") and the link, nothing else. */
export function shareLinks(w: ShareWhat) {
  const url = ORIGIN + w.path, tail = `, on ${SITE}`, site = w.title === SITE;
  const text = w.text ?? clip(w.title, TEXT_MAX - tail.length) + tail;
  const body = [w.title, w.detail, '', url, ...(site ? [] : ['', `Shared from ${SITE}.`])]
    .filter((x) => x !== null).join('\r\n');
  return {
    url, text,
    email: `mailto:?subject=${enc(site ? SITE : `${w.title} · ${SITE}`)}&body=${enc(body)}`,
    // Facebook and LinkedIn take only the link; the preview card it unfurls into names the site.
    networks: [
      ['X', `https://x.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`],
      ['Facebook', `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`],
      ['LinkedIn', `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`],
      ['Bluesky', `https://bsky.app/intent/compose?text=${enc(text + ' ' + url)}`],
      ['Threads', `https://www.threads.com/intent/post?text=${enc(text)}&url=${enc(url)}`],
    ] as [string, string][],
  };
}

/** The clipboard, and the old way where it is missing (a page not served over https). */
async function copy(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true; } catch { /* below */ }
  const t = document.createElement('textarea');
  t.value = text; t.setAttribute('readonly', ''); t.style.position = 'fixed'; t.style.opacity = '0';
  document.body.appendChild(t); t.select();
  let ok = false;
  try { ok = document.execCommand('copy'); } catch { ok = false; }
  t.remove();
  return ok;
}

function save(href: string, file: string) {
  const a = document.createElement('a');
  a.href = href; a.download = file;
  document.body.appendChild(a); a.click(); a.remove();
}

export function ShareButton({ open, onToggle, controls }: { open: boolean; onToggle: () => void; controls: string }) {
  return (
    <button class="btn btn-secondary sr-share-btn" aria-expanded={open} aria-controls={controls} onClick={onToggle}>
      Share
    </button>
  );
}

export function SharePanel({ id, what }: { id: string; what: ShareWhat }) {
  const l = shareLinks(what);
  const [said, say] = useState('');
  const picture = useRef<Blob | null>(null);
  const field = useRef<HTMLInputElement>(null);
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  // The picture is fetched as the panel opens: a phone opens its share menu only while the tap is fresh,
  // and waiting for the file would let that moment pass. Same address as the page, so nothing goes out.
  useEffect(() => {
    picture.current = null; say('');
    if (!what.card) return;
    let live = true;
    fetch(what.card).then((r) => (r.ok ? r.blob() : null)).then((b) => { if (live) picture.current = b; }).catch(() => {});
    return () => { live = false; };
  }, [what.card]);

  const copyLink = async () => {
    if (await copy(l.url)) say('Link copied.');
    else { field.current?.select(); say('Could not copy. The link is selected in the box above: copy it from there.'); }
  };

  const otherApps = () => {
    navigator.share({ title: `${what.title} · ${SITE}`, text: l.text, url: l.url }).catch(() => {});
  };

  // Instagram has no share link for websites and does not make a link in a post clickable: it takes a
  // picture. So the share card goes to the device's share menu (a phone) or is saved (a computer), with
  // the wording and link copied for the caption.
  const instagram = () => {
    const words = `${l.text} ${l.url}`;
    const copied = copy(words);
    const blob = picture.current;
    const file = blob ? new File([blob], what.file, { type: 'image/png' }) : null;
    if (file && navigator.canShare?.({ files: [file] })) {
      navigator.share({ files: [file] }).catch((e) => {
        if (e?.name !== 'AbortError') save(what.card!, what.file);
      });
      copied.then((ok) => say(ok
        ? 'The picture is in your share menu, and the wording and link are copied: choose Instagram, then paste them as the caption.'
        : 'The picture is in your share menu: choose Instagram.'));
      return;
    }
    save(what.card!, what.file);
    copied.then((ok) => say('Instagram has no share link for websites. The picture was saved'
      + (ok ? ' and the wording and link copied: post the picture on Instagram and paste them as the caption.'
        : ': post it on Instagram.')));
  };

  return (
    <div id={id} class="sr-share-panel" role="group" aria-label="Share">
      <div class="sr-share-row">
        <button class="btn btn-secondary" onClick={copyLink}>Copy link</button>
        <a class="btn btn-secondary" href={l.email}>Email</a>
        {l.networks.map(([name, href]) => (
          <a key={name} class="btn btn-secondary" href={href} target="_blank" rel="noopener noreferrer">{name}</a>
        ))}
        {what.card && <button class="btn btn-secondary" onClick={instagram}>Instagram</button>}
        {canShare && <button class="btn btn-secondary" onClick={otherApps}>Other apps</button>}
      </div>
      <input ref={field} class="input sr-share-link" readOnly value={l.url} aria-label="Link to this page"
             onFocus={(e) => e.currentTarget.select()} />
      <div class="sr-share-say" role="status" aria-live="polite">{said}</div>
    </div>
  );
}
